import React from 'react';
import { AppShell } from '../app-shell';
import LoadingIndicator from '../../components/loading-indicator/loading-indicator';
import { ProcessSelection } from '@codametrix/shared-views';
import { sortCaseRuns } from '../../reducers/cases.reducer';
import {
  FlexCard,
  RoundedButton,
  UniversalFeedback
} from '@codametrix/ui-components';
import classnames from 'classnames';

import { Notes } from '@codametrix/shared-views';
import { NotesSidebar } from '../../views/notes-sidebar/notes-sidebar';
import { isValidTargetOrigin } from '../../views/utils/common-utils';

const OMITTED_CATEGORIES = new Set(['']);

export default class CaseDocumentsApp extends React.Component<
  AppProps.CaseDocumentsAppProps,
  {}
> {
  static defaultProps = {
    loading: false,
    dismissFeedback: (message: any) => { }
  };

  constructor(props: AppProps.CaseDocumentsAppProps) {
    super(props);

    this._handleProcessIdChange = this._handleProcessIdChange.bind(this);
    this._handleDismissFeedback = this._handleDismissFeedback.bind(this);
    this.onDocumentSelect = this.onDocumentSelect.bind(this);
  }

  componentDidMount() {
    const url = new URL(window.location.toString());
    const args = {
      caseId: url.searchParams.get('caseId'),
      processId: url.searchParams.get('processInstanceId')
    };
    this.props.getORUs(args);
  }

  onDocumentSelect(docMetadata: CMxAPI.DocumentMetadata) {
    this.props.onSelect(docMetadata);
  }

  _handleDismissFeedback() {
    this.props.dismissFeedback(true);
  }

  _handleProcessIdChange(event: React.MouseEvent) {
    event.preventDefault();
    const target =
      (event.target as HTMLDivElement) || document.createElement('div');
    const linkable = target?.closest('a');
    if (!linkable) return;

    const item = linkable.attributes.getNamedItem('data-process-id');
    if (!item) return;

    const processId = parseInt(item.value);
    if (!processId) return;

    if (processId === this.props.activeCaseRun.process_instance_id) return;

    const caseRuns = this.props.activeCase?.caseRuns;

    if (!caseRuns) return;

    const soughtRun = caseRuns.find(
      caseRun => caseRun.process_instance_id === processId
    );

    if (!soughtRun) return;
    this.props.setActiveProcess(soughtRun);
  }

  render() {
    const { props } = this;
    const {
      activeCase,
      activeCaseRun,
      locked,
      loading,
      activeNotes,
      documentMetadata,
      feedback
    } = props;
    const caseRuns = activeCase?.caseRuns ? sortCaseRuns(activeCase, {}) : {};
    const [activeNote] = activeNotes;
    const docs = documentMetadata.filter(
      doc => !OMITTED_CATEGORIES.has(doc.documentCategory)
    );

    const handleDownload = (event: React.MouseEvent) => {
      event.preventDefault();
      const targetOrgin = window.location.origin;
      // validate Target Origin
      if (isValidTargetOrigin(targetOrgin)) {
        var link = document.createElement('a');
        const isPdf = activeNote.filetype === "application/pdf";

        if (isPdf) {
          const base64 = activeNote.content.replace(/^data:.*;base64,/, '');
          const pdfbytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
          const pdfblob = new Blob([pdfbytes], { type: 'application/pdf' });
          const url = URL.createObjectURL(pdfblob);
          link.setAttribute('href', url);
          link.setAttribute('download', activeNote.filename ?? 'note.pdf');
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else {
          link.setAttribute(
            'href',
            'data:text/plain;charset=utf-8,' +
            encodeURIComponent(activeNote.content)
          )
          link.setAttribute('download', `${activeNote.filename ?? 'note.txt'}`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    };

    const styleClasses = ['centered-application', 'case-documents-view'];
    let inner;
    let header;

    if (locked) {
      styleClasses.push(`cmx-application-locked`);
    }

    if (loading) {
      styleClasses.push(
        `cmx-application-masked`,
        `cmx-application-loading`,
        'loading-view'
      );
      inner = <LoadingIndicator text={`Loading Documents...`} />;
    } else if (activeNote !== undefined) {
      styleClasses.push(`cmx-application-loaded`);

      header = (
        <div>
          <div className="case-docs-header cols">
            <div className="case-docs-header-left">
              <h5>
                Documents for process instance:{' '}
                {activeCaseRun.process_instance_id}
              </h5>
            </div>
            <div className="case-docs-header-right case-docs-header-buttons">
              <RoundedButton
                className="btn-primary btn-sm active download-btn btn"
                type="submit"
                title="Download Selected Document"
                onClick={handleDownload}>
                Download
              </RoundedButton>
              <ProcessSelection
                caseRuns={caseRuns}
                activeId={activeCaseRun.process_instance_id}
                onClick={this._handleProcessIdChange}
              />
            </div>
          </div>
          <div className="created-on">
            <h6>Created on {activeCaseRun.datetime_of_service}</h6>
          </div>
        </div>
      );

      inner = (
        <FlexCard header={header} className="case-documents-card oru">
          <div className="notes-view">
            {!!docs?.length && (
              <div className="doc-side-nav">
                  <NotesSidebar
                    onSelect={this.onDocumentSelect}
                    activeNote={activeNote}
                    documentMetadata={docs}
                  />
              </div>
            )}
            <div className="notes-content">
            <Notes notes={[activeNote]} />
          </div>
          </div>
        </FlexCard>
      );
    } else {
      styleClasses.push(`cmx-application-loaded`);
      header = (
        <div className="case-docs-header cols">
          <ProcessSelection
            caseRuns={caseRuns}
            activeId={activeCaseRun.process_instance_id}
            onClick={this._handleProcessIdChange}
          />
        </div>
      );
      inner = (
        <FlexCard header={header} className="case-documents-card no-notes">
          <br />
          <h5>Document not available</h5>
        </FlexCard>
      );
    }

    return (
      <AppShell
        registerActivity={props.registerActivity}
        className="case-documents-app">
        <div className={classnames(styleClasses)}>
          {locked && (
            <div className="locked-message">
              The case details you were viewing are no longer active.
            </div>
          )}
          <div className="centered-application-wrapper">
            {feedback && (
              <UniversalFeedback
                dismissFeedback={this._handleDismissFeedback}
                feedback={feedback}
                showFeedback={feedback ? true : false}
              />
            )}
            {inner}
          </div>
        </div>
      </AppShell>
    );
  }
}
